import { useState, type ReactNode } from 'react';
import { useActiveClassroom } from '../context/ActiveClassroomProvider.js';
import { useClock } from '../clock/ClockProvider.js';
import { ClockPicker } from '../clock/ClockPicker.js';
import { NoiseMeterButton } from './NoiseMeter/index.js';
import styles from './LiveClock.module.css';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatWallClock(epochMs: number): { time: string; meridiem: string } {
  const d = new Date(epochMs);
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return {
    time: `${hours}:${pad2(minutes)}`,
    meridiem,
  };
}

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${pad2(m)}:${pad2(s)}`;
  return `${m}:${pad2(s)}`;
}

/**
 * Live-mode clock pinned to the bottom-left corner. Three modes:
 *   idle       — wall clock + small bell glyph if alarms are set
 *   countdown  — accent-orange pill, "Name · M:SS" remaining
 *   timer      — soft-green pill, "Name · M:SS" elapsed
 *
 * Tapping opens the ClockPicker (Countdown / Timer / Alarms tabs).
 * Hidden when no classroom is active.
 */
export function LiveClock() {
  const { active } = useActiveClassroom();
  const clock = useClock();
  const [pickerOpen, setPickerOpen] = useState(false);

  if (!active) return null;

  // Active-state wash applied to the pill container (countdown / timer / none).
  let washClass = '';
  let body: ReactNode;
  let testId = 'live-clock-idle';

  if (clock.active.kind === 'countdown') {
    washClass = styles.clockCountdown ?? '';
    const remaining = Math.max(0, clock.active.startedAt + clock.active.durationMs - clock.now);
    testId = 'live-clock-countdown';
    body = (
      <>
        <span className={styles.label}>{clock.active.name}</span>
        <span className={styles.time}>{formatDuration(remaining)}</span>
      </>
    );
  } else if (clock.active.kind === 'timer') {
    washClass = styles.clockTimer ?? '';
    const elapsed = clock.now - clock.active.startedAt;
    testId = 'live-clock-timer';
    body = (
      <>
        <span className={styles.label}>{clock.active.name}</span>
        <span className={styles.time}>{formatDuration(elapsed)}</span>
      </>
    );
  } else {
    const wall = formatWallClock(clock.now);
    body = (
      <>
        <span className={styles.time}>{wall.time}</span>
        <span className={styles.meridiem}>{wall.meridiem}</span>
        {clock.alarms.length > 0 ? (
          <span
            className={styles.alarmBadge}
            aria-label={`${clock.alarms.length} alarm${clock.alarms.length === 1 ? '' : 's'} set`}
          >
            🔔
          </span>
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className={`${styles.dock} ${washClass}`}>
        <button
          type="button"
          className={styles.timeBtn}
          onClick={() => setPickerOpen(true)}
          aria-label="Open clock"
          data-testid={testId}
        >
          {body}
        </button>
        <span className={styles.sep} aria-hidden="true" />
        <NoiseMeterButton />
      </div>
      <ClockPicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
    </>
  );
}
